import {
  SearchField,
  SearchFieldClear,
  SearchFieldInput,
} from "@/components/ui/controls/searchfield";
import { cn } from "@/lib/utils";
import { MagnifyingGlassIcon, XCircleIcon, XIcon } from "@phosphor-icons/react";
import type React from "react";

type SearchBarProps = {
  /** The current search term value displayed in the input field */
  searchTerm: string;
  /** The title used to generate the placeholder text (e.g., 'filters' becomes 'Search filters') */
  title: string;
  /** Callback function triggered when the search input value changes */
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Callback function triggered when the clear button is clicked */
  onClearSearch: () => void;
  /** Whether the search bar is in an active/open state (affects tabIndex accessibility) */
  isOpen: boolean;
  /** Additional CSS classes to apply to the container div */
  className?: string;
};

const SearchBar: React.FC<SearchBarProps> = ({
  searchTerm,
  title,
  onSearchChange,
  onClearSearch,
  isOpen,
  className,
}) => {
  return (
    <div className={cn("bg-white z-10 pl-1 pt-0.5 pr-0.5", className)}>
      <SearchField aria-label="search">
        <MagnifyingGlassIcon
          aria-hidden
          size={12}
          className="text-slate-300 mb-1.5"
        />
        <SearchFieldInput
          className="pl-1 text-ellipsis bg-white pt-0"
          placeholder={`Search ${title.toLowerCase()}`}
          value={searchTerm}
          onChange={onSearchChange}
          tabIndex={isOpen ? 0 : -1}
        />
        {searchTerm && (
          <SearchFieldClear onPress={onClearSearch}>
            <XIcon aria-hidden className="text-gray-400" size={14} />
          </SearchFieldClear>
        )}
      </SearchField>
    </div>
  );
};

type ModalSearchBarProps = {
  /** The current search term value displayed in the input field */
  searchTerm: string;
  /** The title used to generate the placeholder text (e.g., 'clusters' becomes 'Search clusters...') */
  title: string;
  /** Callback function triggered when the search input value changes */
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Callback function triggered when the clear button is clicked */
  onClearSearch: () => void;
  /** Additional CSS classes to apply to the container */
  className?: string;
};

/**
 * ModalSearchBar component for use in modals
 * Features: 14px icon, gray-900 text, blue underline when typing, XCircle clear button
 */
export const ModalSearchBar: React.FC<ModalSearchBarProps> = ({
  searchTerm,
  title,
  onSearchChange,
  onClearSearch,
  className,
}) => {
  const titleLower = (title || "clusters").toLowerCase();
  return (
    <SearchField
      className={cn(
        "flex flex-row outline-hidden border-none bg-transparent focus:ring-0 placeholder-gray-400 items-center",
        className,
      )}
      aria-label={`Search ${titleLower}`}
    >
      <MagnifyingGlassIcon
        aria-hidden
        size={16}
        className={searchTerm ? "text-gray-900" : "text-slate-300"}
      />
      <div
        className={cn(
          "flex w-full ml-2 border-b",
          searchTerm ? "border-blue-600" : "border-gray-200",
        )}
      >
        <SearchFieldInput
          placeholder={`Search ${titleLower}...`}
          value={searchTerm}
          onChange={onSearchChange}
          className="px-0 text-xs text-gray-900"
        />
        {searchTerm && (
          <SearchFieldClear onPress={onClearSearch}>
            <XCircleIcon
              className="text-gray-400"
              size={14}
              weight="fill"
              aria-hidden
            />
          </SearchFieldClear>
        )}
      </div>
    </SearchField>
  );
};

export default SearchBar;
